// src/pages/investor/InvestorHoldings.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import investorService from '../../services/investorService';
import { useToast } from '../../hooks/useToast';
import Button from '../../components/common/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCoins, faGavel, faClock, faArrowRight, faXmark, faTimeline,
} from '@fortawesome/free-solid-svg-icons';
import { extractApiList, extractApiPagination } from '../../utils/responseAdapters';
import Modal from '../../components/common/Modal';

const HOLDING_DETAIL_CACHE_TTL_MS = 5 * 60 * 1000;
const holdingDetailCache = new Map();

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ─── Token Event Timeline ────────────────────────────────────────────────────

function TokenEventTimeline({ events }) {
  if (!events || events.length === 0) {
    return <p style={{ color: '#888', fontSize: 14 }}>No provenance events recorded.</p>;
  }

  return (
    <div style={{ position: 'relative', paddingLeft: 24 }}>
      <div style={{
        position: 'absolute', left: 8, top: 0, bottom: 0,
        width: 2, background: '#e5e7eb',
      }} />
      {events.map((event, idx) => (
        <div key={idx} style={{ marginBottom: 20, position: 'relative' }}>
          <div style={{
            position: 'absolute', left: -20, top: 4, width: 12, height: 12,
            borderRadius: '50%', background: '#2d8cf0', border: '2px solid #fff',
          }} />
          <div style={{ fontSize: 12, color: '#6b7280' }}>{formatDate(event.timestamp || event.created_at)}</div>
          <div style={{ fontWeight: 600, fontSize: 14, marginTop: 2 }}>
            {event.event_type || event.type || 'Event'}
          </div>
          {event.description && <div style={{ fontSize: 13, color: '#555', marginTop: 2 }}>{event.description}</div>}
          {event.amount && <div style={{ fontSize: 13, color: '#10b981', fontWeight: 600 }}>₹{Number(event.amount).toLocaleString('en-IN')}</div>}
        </div>
      ))}
    </div>
  );
}

// ─── Holding Detail Modal ─────────────────────────────────────────────────────

function HoldingDetailModal({ tokenId, onClose }) {
  const { showToast } = useToast();
  const [holding, setHolding] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    const cached = holdingDetailCache.get(tokenId);
    if (cached && (Date.now() - cached.cachedAt) < HOLDING_DETAIL_CACHE_TTL_MS) {
      setHolding(cached.holding);
      setEvents(cached.events || []);
      setLoading(false);
      return () => { active = false; };
    }

    const load = async () => {
      try {
        const [holdRes, eventsRes] = await Promise.allSettled([
          investorService.getHoldingForToken(tokenId),
          investorService.getTokenEventLog(tokenId),
        ]);
        if (!active) return;
        const nextHolding = holdRes.status === 'fulfilled'
          ? (holdRes.value?.data?.data || holdRes.value?.data)
          : null;
        const nextEvents = eventsRes.status === 'fulfilled'
          ? extractApiList(eventsRes.value?.data)
          : [];

        if (nextHolding) setHolding(nextHolding);
        setEvents(nextEvents);

        if (nextHolding) {
          holdingDetailCache.set(tokenId, {
            cachedAt: Date.now(),
            holding: nextHolding,
            events: nextEvents,
          });
        }
      } catch {
        showToast('Failed to load holding details', 500);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [tokenId, showToast]);

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Holding Details"
      subtitle="Token allocation & provenance history"
      maxWidth={580}
    >
      {loading ? (
        <p style={{ color: 'var(--text-muted)' }}>Loading details…</p>
      ) : (
        <>
          {holding && (
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 24,
              background: 'var(--bg-surface, #0f1117)', borderRadius: 'var(--radius-lg, 16px)', padding: '1.25rem',
              border: '1px solid var(--border-hairline, rgba(255, 255, 255, 0.08))',
            }}>
              <div>
                <div style={{ fontSize: 12, color: 'var(--text-muted, #64748b)' }}>Token</div>
                <div style={{ fontWeight: 700, color: 'var(--text-primary, #f8fafc)', fontSize: '1.1rem' }}>
                  {holding.token_symbol || holding.token_id}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: 'var(--text-muted, #64748b)' }}>Quantity</div>
                <div className="tabular-nums font-mono" style={{ fontWeight: 700, color: 'var(--text-primary, #f8fafc)', fontSize: '1.1rem' }}>
                  {holding.quantity ?? holding.sub_tokens_owned ?? '—'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: 'var(--text-muted, #64748b)' }}>Avg. Price</div>
                <div className="tabular-nums font-mono" style={{ fontWeight: 700, color: 'var(--text-primary, #f8fafc)', fontSize: '1.1rem' }}>
                  ₹{Number(holding.avg_price || 0).toLocaleString('en-IN')}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: 'var(--text-muted, #64748b)' }}>Current Value</div>
                <div className="tabular-nums font-mono" style={{ fontWeight: 700, color: '#10b981', fontSize: '1.1rem' }}>
                  ₹{Number(holding.current_value || 0).toLocaleString('en-IN')}
                </div>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <FontAwesomeIcon icon={faTimeline} style={{ color: 'var(--accent-cyan, #38bdf8)' }} />
            <h4 style={{ margin: 0, color: 'var(--text-primary, #f8fafc)' }}>Provenance</h4>
          </div>
          <TokenEventTimeline events={events} />
        </>
      )}
    </Modal>
  );
}

// ─── Holdings Page ────────────────────────────────────────────────────────────

export default function InvestorHoldings() {
  const { showToast } = useToast();
  const [holdings, setHoldings] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [detailTokenId, setDetailTokenId] = useState(null);

  const fetchHoldings = useCallback(async (nextCursor = null) => {
    try {
      const res = await investorService.getHoldings({ limit: 20, cursor: nextCursor || undefined });
      const data = res?.data?.data || res?.data;
      const items = extractApiList(data);
      const pagination = extractApiPagination(data) || data?.pagination;
      if (nextCursor) {
        setHoldings((prev) => [...prev, ...items]);
      } else {
        setHoldings(items);
      }
      setCursor(pagination?.next_cursor || null);
      setHasMore(pagination?.has_more || false);
    } catch {
      showToast('Failed to load holdings', 500);
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchHoldings(); }, [fetchHoldings]);

  const handlePushToBid = async (tokenId) => {
    try {
      await investorService.pushTokenToBid(tokenId);
      showToast('Token pushed to bid', 200);
      fetchHoldings();
    } catch {
      showToast('Failed to push token to bid', 500);
    }
  };

  return (
    <DashboardLayout>
      <div style={{ padding: '24px 20px' }}>
        <h2 style={{ marginTop: 0, marginBottom: 20 }}>My Holdings</h2>

        {loading ? <p>Loading holdings…</p> : holdings.length === 0 ? (
          <div style={{
            padding: 40, textAlign: 'center', background: '#f9fafb',
            borderRadius: 12, color: '#888',
          }}>
            <FontAwesomeIcon icon={faCoins} style={{ fontSize: 32, marginBottom: 12 }} />
            <div>No token holdings yet. Win tokens through auctions to see them here.</div>
          </div>
        ) : (
          <>
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16,
            }}>
              {holdings.map((h) => {
                const tokenId = h.token_id || h.id;
                return (
                  <div key={tokenId} className="application-card" style={{ cursor: 'pointer' }}
                    onClick={() => setDetailTokenId(tokenId)}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 16 }}>{h.token_symbol || h.art_title || tokenId}</div>
                        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                          {h.quantity ?? h.sub_tokens_owned ?? 0} tokens
                        </div>
                      </div>
                      <span style={{
                        background: h.bid_pushed ? '#10b98122' : '#f59e0b22',
                        color: h.bid_pushed ? '#10b981' : '#f59e0b',
                        padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                      }}>
                        {h.bid_pushed ? 'On Bid' : 'Held'}
                      </span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 12 }}>
                      <div>
                        <div style={{ fontSize: 11, color: '#9ca3af' }}>Avg Price</div>
                        <div style={{ fontWeight: 600 }}>₹{Number(h.avg_price || 0).toLocaleString('en-IN')}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: '#9ca3af' }}>Value</div>
                        <div style={{ fontWeight: 600, color: '#10b981' }}>
                          ₹{Number(h.current_value || 0).toLocaleString('en-IN')}
                        </div>
                      </div>
                    </div>
                    <div style={{ marginTop: 12, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      {!h.bid_pushed && (
                        <Button variant="secondary" onClick={(e) => { e.stopPropagation(); handlePushToBid(tokenId); }}>
                          <FontAwesomeIcon icon={faGavel} style={{ marginRight: 4 }} />
                          Push to Bid
                        </Button>
                      )}
                      <Button variant="primary" onClick={(e) => { e.stopPropagation(); setDetailTokenId(tokenId); }}>
                        <FontAwesomeIcon icon={faArrowRight} />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>

            {hasMore && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
                <Button variant="secondary" onClick={() => fetchHoldings(cursor)} disabled={loading}>
                  Load More
                </Button>
              </div>
            )}
          </>
        )}

        {detailTokenId && (
          <HoldingDetailModal tokenId={detailTokenId} onClose={() => setDetailTokenId(null)} />
        )}
      </div>
    </DashboardLayout>
  );
}
